// ============================================
// APP: NVA (React App)
// FILE: src/components/GuestLoginModal.tsx
// PURPOSE: Il login dell'ospite, a tutto schermo (mockup approvato
//          "Accesso"): prima una finestra al centro, ora un foglio sopra
//          qualunque pagina nello stile delle pagine dei servizi
//          (ServicePageLayout): testata corta blu → indaco, "‹" che chiude,
//          i due campi in alto (restano visibili con la tastiera), sotto gli
//          avvisi e gli errori nell'ordine di sempre (avviso → errore →
//          contatto/consiglio → collega), in fondo la barra fissa con
//          Annulla e Accedi.
//
//          Resta un Dialog di Radix (focus intrappolato, Esc, niente
//          scorrimento sotto), solo vestito a tutto schermo e sopra la barra
//          delle schede. Le props sono quelle di sempre: i chiamanti
//          (HomeSessionHost, VipTicketCard, MyStay, Extras...) non cambiano.
//          Nessuna schermata di conferma: riuscito l'accesso si chiude e il
//          chiamante porta dove serve, come prima.
// ============================================

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Info,
  KeyRound,
  Link2,
  Loader2,
  LogIn,
  Mail,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getBookingClaimStatus, type BookingClaimStatus } from '../lib/guestApi';
import { readStoredClaim, clearStoredClaim } from '../lib/bookingClaimStorage';
import { clearExpiredClaim, latestPendingClaim, readExpiredClaim } from '../lib/checkInClaimStorage';
import { useStatusBarOverlay } from '../lib/statusBar';
import { cn } from '../lib/utils';
import { BackButton, HeroTile, SERVICE_BUTTON, SERVICE_NOTICE } from '../pages/services/ServicePageLayout';

interface GuestLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (bookingReference: string, email: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  /**
   * Channel-manager error code (e.g. `booking_no_show`,
   * `booking_cancelled_refunded`, `booking_cancelled`). When present and
   * `booking_*`, we render a localized booking-status message instead of
   * the generic `error` string and hide the "Go to Check-in" hint (the
   * external check-in won't help — the booking is dead).
   */
  errorCode?: string | null;
  redirectToChat?: boolean;
  /**
   * Codice ed email da mettere nei campi all'apertura (la sessione scaduta
   * nella chat: "Accedi di nuovo"). Vincono sui dati del check-in online;
   * un campo che l'ospite ha già scritto resta com'è.
   */
  initialBookingReference?: string | null;
  initialEmail?: string | null;
}

interface ClaimBanner {
  status: BookingClaimStatus;
  email: string;
  decisionNote?: string;
  loginReference?: string;
}

/** I controlli nel telefono, prima di chiamare il server (login.errors.*). */
type LocalError = 'enterBookingRef' | 'enterEmail' | 'invalidEmail';

const bookingMessageKeyForErrorCode = (code: string | null | undefined) => {
  switch (code) {
    case 'booking_no_show':            return 'noShow';
    case 'booking_cancelled_refunded': return 'cancelledRefunded';
    case 'booking_cancelled':          return 'cancelled';
    default:                           return 'default';
  }
};

/** La barra di stato in chiaro: il primo colore della testata (blue-600). */
export const LOGIN_THEME_COLOR = '#2563EB';

const ERROR_ID = 'login-error';

// ---- Colori ----------------------------------------------------------------------

type Tone = 'amber' | 'green' | 'red';

/** Gli avvisi: fondo e bordo, icona, titolo e testo (in scuro i toni -950 e -100/-300). */
const TONES: Record<Tone, { box: string; icon: string; title: string; text: string }> = {
  amber: {
    box: 'border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/40',
    icon: 'text-amber-700 dark:text-amber-300',
    title: 'text-amber-900 dark:text-amber-100',
    text: 'text-amber-800 dark:text-amber-200',
  },
  green: {
    box: 'border-green-200 bg-green-50 dark:border-green-900/60 dark:bg-green-950/40',
    icon: 'text-green-700 dark:text-green-300',
    title: 'text-green-900 dark:text-green-100',
    text: 'text-green-800 dark:text-green-200',
  },
  red: {
    box: 'border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40',
    icon: 'text-red-700 dark:text-red-300',
    title: 'text-red-900 dark:text-red-100',
    text: 'text-red-800 dark:text-red-200',
  },
};

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-nva-bone dark:focus-visible:ring-blue-400 dark:focus-visible:ring-offset-stone-950';

/** Il collegamento dentro un avviso ("Riprova", "Vai al Check-in"): alto 44px, maiuscolo. */
const TEXT_LINK = cn(
  'inline-flex min-h-[44px] items-center gap-1.5 self-start rounded-md text-[12.5px] font-extrabold uppercase leading-tight tracking-button',
  FOCUS,
);

/** Il bottone chiaro a tutta larghezza (Contattaci, Collega la prenotazione). */
const SOFT_BUTTON = cn(
  SERVICE_BUTTON,
  'w-full border-2 border-blue-200 bg-blue-50 text-blue-700 no-underline',
  'dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200',
);

const INPUT = cn(
  'block h-12 w-full rounded-xl border-[1.5px] bg-nva-card pl-11 pr-3.5 text-[16px] text-nva-espresso outline-none',
  'placeholder:font-normal placeholder:normal-case placeholder:tracking-normal placeholder:text-stone-500',
  'focus:border-blue-600 focus:ring-[3px] focus:ring-blue-600/20',
  'dark:bg-stone-900 dark:text-stone-100 dark:placeholder:text-stone-400 dark:focus:border-blue-400 dark:focus:ring-blue-400/30',
);

const inputState = (invalid: boolean, busy: boolean) => cn(
  invalid
    ? 'border-red-600 ring-[3px] ring-red-600/10 dark:border-red-400 dark:ring-red-400/20'
    : 'border-[#9A9185] dark:border-stone-600',
  busy && 'cursor-default bg-[#F4EFE6] text-stone-600 dark:bg-stone-800 dark:text-stone-400',
);

// ---- Pezzi -----------------------------------------------------------------------

const Notice: React.FC<{
  tone: Tone;
  icon: React.ReactNode;
  title?: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
  role?: string;
  id?: string;
}> = ({ tone, icon, title, children, action, role, id }) => {
  const colors = TONES[tone];
  return (
    <div role={role} id={id} className={cn(SERVICE_NOTICE, 'flex items-start gap-2.5', colors.box, action && 'pb-1')}>
      <span aria-hidden="true" className={cn('mt-px flex flex-shrink-0 [&>svg]:h-[18px] [&>svg]:w-[18px]', colors.icon)}>
        {icon}
      </span>
      <div className="flex min-w-0 flex-col gap-[3px]">
        {title ? <p className={cn('m-0 text-[14px] font-extrabold leading-[1.35]', colors.title)}>{title}</p> : null}
        <p className={cn('m-0 text-[14px] leading-[1.45]', colors.text)}>{children}</p>
        {action}
      </div>
    </div>
  );
};

interface FieldProps {
  id: string;
  label: string;
  hint: string;
  icon: React.ReactNode;
  invalid: boolean;
  busy: boolean;
  className?: string;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
}

const Field: React.FC<FieldProps> = ({ id, label, hint, icon, invalid, busy, className, inputProps }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-[14.5px] font-bold leading-snug text-nva-espresso dark:text-stone-100">
      {label}
    </label>
    <div className="relative">
      <span aria-hidden="true" className="pointer-events-none absolute left-3.5 top-3.5 flex text-stone-600 dark:text-stone-400 [&>svg]:h-5 [&>svg]:w-5">
        {icon}
      </span>
      <input
        id={id}
        {...inputProps}
        // Durante l'accesso i campi restano a fuoco ma non si scrive (come i bottoni).
        readOnly={busy}
        aria-disabled={busy || undefined}
        aria-invalid={invalid}
        aria-describedby={invalid ? `${id}-hint ${ERROR_ID}` : `${id}-hint`}
        className={cn(INPUT, inputState(invalid, busy), className)}
      />
    </div>
    <span id={`${id}-hint`} className="text-[12.5px] leading-[1.35] text-stone-600 dark:text-stone-400">
      {hint}
    </span>
  </div>
);

// ---- Il login ----------------------------------------------------------------------

const GuestLoginModal: React.FC<GuestLoginModalProps> = ({
  isOpen,
  onClose,
  onLogin,
  isLoading,
  error,
  errorCode,
  redirectToChat = false,
  initialBookingReference,
  initialEmail,
}) => {
  const { t } = useTranslation();
  const [bookingReference, setBookingReference] = useState('');
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState<LocalError | null>(null);
  const navigate = useNavigate();
  const [claimBanner, setClaimBanner] = useState<ClaimBanner | null>(null);
  // Il check-in online partito da questo telefono: in attesa dello
  // smistamento (entrerà da solo) o sparito ("collegamento scaduto").
  const [checkInClaimBanner, setCheckInClaimBanner] = useState<'pending' | 'expired' | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  // All'apertura: riferimento ed email scritti nel check-in sono già nei
  // campi. Dopo lo smistamento il channel manager li accetta per il login.
  // Quelli passati dal chiamante (sessione scaduta nella chat) vengono prima.
  useEffect(() => {
    if (!isOpen) return;
    const pending = latestPendingClaim();
    const expired = pending ? null : readExpiredClaim();
    const source = pending || expired;
    setCheckInClaimBanner(pending ? 'pending' : expired ? 'expired' : null);
    const ref = initialBookingReference || source?.savedBookingRef;
    const mail = initialEmail || source?.savedEmail;
    if (ref) setBookingReference((current) => current || ref.toUpperCase());
    if (mail) setEmail((current) => current || mail);
  }, [isOpen, initialBookingReference, initialEmail]);

  // All'apertura si controlla l'esito di una richiesta già inviata: se
  // approvata, riferimento ed email sono già pronti nei campi del login.
  useEffect(() => {
    if (!isOpen) return;
    const stored = readStoredClaim();
    if (!stored) {
      setClaimBanner(null);
      return;
    }
    let cancelled = false;
    getBookingClaimStatus(stored.requestId, stored.email).then((response) => {
      if (cancelled) return;
      if (!response.success || !response.status) return;
      setClaimBanner({
        status: response.status,
        email: stored.email,
        decisionNote: response.decisionNote,
        loginReference: response.loginReference,
      });
      if (response.status === 'approved') {
        if (response.loginReference) setBookingReference(response.loginReference.toUpperCase());
        setEmail(stored.email);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  // La barra di stato (in chiaro) prende il blu della testata finché il
  // foglio è aperto; chiuso, torna quella della pagina sotto, anche se
  // intanto si è cambiata pagina (lib/statusBar).
  useStatusBarOverlay(LOGIN_THEME_COLOR, isOpen);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Un secondo Invio mentre si aspetta il server non rimanda la richiesta.
    if (isLoading) return;
    setLocalError(null);

    // Validation
    if (!bookingReference.trim()) {
      setLocalError('enterBookingRef');
      return;
    }
    if (!email.trim()) {
      setLocalError('enterEmail');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError('invalidEmail');
      return;
    }

    const success = await onLogin(bookingReference.trim(), email.trim().toLowerCase());

    if (success) {
      // Reset form
      setBookingReference('');
      setEmail('');
      setLocalError(null);
      // Il login funziona: la richiesta di collegamento ha esaurito il suo scopo.
      // (Le richieste del check-in online le toglie già la sessione.)
      clearStoredClaim();
      clearExpiredClaim();
      setClaimBanner(null);
      setCheckInClaimBanner(null);
      onClose();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setBookingReference('');
      setEmail('');
      setLocalError(null);
      onClose();
    }
  };

  // Il modulo di collegamento ha una pagina sua, a tutto schermo: qui si
  // chiude il foglio e si porta con sé l'email già digitata.
  const goToClaim = () => {
    const prefill = email.trim();
    handleClose();
    navigate('/claim-booking', { state: { email: prefill } });
  };

  const isBookingError = !localError && !!errorCode && errorCode.startsWith('booking_');
  const bookingMessageKey = bookingMessageKeyForErrorCode(errorCode);
  const displayError = localError
    ? t(`login.errors.${localError}`)
    : (isBookingError ? t(`bookingInvalidated.message.${bookingMessageKey}`) : error);
  const contactEmail = t('bookingInvalidated.contactEmail');
  // Il collegamento si propone solo quando la prenotazione non è stata
  // trovata: con un errore `booking_*` la prenotazione esiste ma è morta.
  const canClaim = !localError && !!error && !isBookingError;
  const refInvalid = localError === 'enterBookingRef';
  const emailInvalid = localError === 'enterEmail' || localError === 'invalidEmail';
  const busy = isLoading;

  const claimTone: Tone = claimBanner?.status === 'approved' ? 'green' : claimBanner?.status === 'rejected' ? 'red' : 'amber';

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogPrimitive.Portal>
        {/* Lo sfondo di Radix: blocca lo scorrimento della pagina sotto (come la finestra di prima) */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-nva-bone dark:bg-stone-950" />
        <DialogPrimitive.Content
          aria-busy={busy || undefined}
          // Si arriva sul titolo (come nelle pagine a tutto schermo): il
          // lettore di schermo annuncia il login e la tastiera non copre gli avvisi.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            titleRef.current?.focus({ preventScroll: true });
          }}
          className="fixed inset-0 z-[100] flex flex-col bg-nva-bone text-nva-espresso outline-none dark:bg-stone-950 dark:text-stone-100 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
        >
          <form onSubmit={handleSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
            <div
              className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
            >
              <div className="flex min-h-full flex-col">
                {/* Testata corta: blu → indaco come il bottone di sempre. */}
                <header
                  // Sotto la barra di stato con --nva-screen-top e non --safe-area-inset-top:
                  // la riga della consegna azzera quella variabile, ma il foglio le sta sopra.
                  // Nell'app installata su iPhone comprende la striscia dell'orologio.
                  className="relative flex flex-shrink-0 flex-col overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-600 px-4 pb-[38px] pt-[calc(var(--nva-screen-top,0px)+16px)]"
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-[130px] -top-[140px] h-[280px] w-[280px] rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(99,102,241,.55) 0%, rgba(99,102,241,.2) 45%, rgba(99,102,241,0) 70%)' }}
                  />
                  <div className="relative flex items-center justify-between gap-3">
                    <BackButton
                      onClick={handleClose}
                      label={t('common.close')}
                      disabled={busy}
                      className="focus-visible:ring-offset-blue-700"
                    />
                    <HeroTile>
                      <LogIn strokeWidth={1.8} />
                    </HeroTile>
                  </div>

                  <div className="relative mt-3.5 flex flex-col gap-2 px-1">
                    <span aria-hidden="true" className="block h-0.5 w-10 bg-indigo-300" />
                    <span className="block text-[11px] font-extrabold uppercase leading-snug tracking-[0.2em] text-indigo-50">
                      {t('common.appName')}
                    </span>
                    <DialogPrimitive.Title asChild>
                      <h1
                        ref={titleRef}
                        tabIndex={-1}
                        className="m-0 break-words font-display text-[32px] font-bold leading-[1.05] text-white outline-none"
                      >
                        {t('login.title')}
                      </h1>
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="m-0 text-[15px] leading-[1.4] text-white">
                      {redirectToChat
                        ? t('login.descriptionChat')
                        : t('login.description')}
                    </DialogPrimitive.Description>
                  </div>
                </header>

                {/* Il foglio: prima i due campi, poi avvisi ed errori. */}
                <div className="relative -mt-5 flex flex-grow flex-col gap-3.5 rounded-t-[22px] bg-nva-bone px-4 pb-7 pt-[22px] dark:bg-stone-950">
                  <Field
                    id="bookingRef"
                    label={t('login.bookingReference')}
                    hint={t('login.bookingRefHint')}
                    icon={<KeyRound strokeWidth={2} />}
                    invalid={refInvalid}
                    busy={busy}
                    className="font-bold uppercase tracking-[0.06em]"
                    inputProps={{
                      type: 'text',
                      placeholder: t('login.bookingRefPlaceholder'),
                      value: bookingReference,
                      onChange: (e) => setBookingReference(e.target.value.toUpperCase()),
                      autoComplete: 'off',
                      autoCapitalize: 'characters',
                      spellCheck: false,
                    }}
                  />

                  <Field
                    id="email"
                    label={t('login.emailAddress')}
                    hint={t('login.emailHint')}
                    icon={<Mail strokeWidth={2} />}
                    invalid={emailInvalid}
                    busy={busy}
                    inputProps={{
                      type: 'email',
                      inputMode: 'email',
                      placeholder: t('login.emailPlaceholder'),
                      value: email,
                      onChange: (e) => setEmail(e.target.value),
                      autoComplete: 'email',
                    }}
                  />

                  {checkInClaimBanner && (
                    <Notice
                      role="status"
                      tone="amber"
                      icon={checkInClaimBanner === 'pending' ? <Clock strokeWidth={2.2} /> : <Info strokeWidth={2.2} />}
                      title={checkInClaimBanner === 'pending' ? t('checkInClaim.pendingTitle') : undefined}
                    >
                      {checkInClaimBanner === 'pending' ? t('checkInClaim.loginBanner') : t('checkInClaim.expired')}
                    </Notice>
                  )}

                  {claimBanner && (
                    <Notice
                      role="status"
                      tone={claimTone}
                      icon={claimBanner.status === 'approved'
                        ? <CheckCircle2 strokeWidth={2.2} />
                        : claimBanner.status === 'rejected'
                          ? <AlertCircle strokeWidth={2.2} />
                          : <Clock strokeWidth={2.2} />}
                      title={t(`claim.banner.${claimBanner.status}Title`)}
                      action={claimBanner.status === 'rejected' ? (
                        <button
                          type="button"
                          onClick={() => {
                            // Si riparte da zero: il vecchio record non serve più.
                            clearStoredClaim();
                            setClaimBanner(null);
                            goToClaim();
                          }}
                          className={cn(TEXT_LINK, TONES.red.text)}
                        >
                          {t('claim.banner.retry')}
                          <ArrowRight className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />
                        </button>
                      ) : undefined}
                    >
                      {claimBanner.status === 'approved'
                        ? t('claim.banner.approvedText', { loginReference: claimBanner.loginReference || '' })
                        : claimBanner.status === 'rejected'
                          ? (claimBanner.decisionNote || t('claim.banner.rejectedText'))
                          : t('claim.banner.pendingText', { email: claimBanner.email })}
                    </Notice>
                  )}

                  {displayError && (
                    <>
                      <div role="alert" id={ERROR_ID} className={cn(SERVICE_NOTICE, 'flex items-start gap-2.5', TONES.red.box)}>
                        <AlertCircle className="mt-px h-[18px] w-[18px] flex-shrink-0 text-red-700 dark:text-red-300" strokeWidth={2.2} aria-hidden="true" />
                        <p className="m-0 text-[14px] font-semibold leading-[1.45] text-red-700 dark:text-red-200">{displayError}</p>
                      </div>
                      {/* For booking_* errors: contact CTA only (Go-to-Check-In would
                          not help — the booking is invalidated server-side). */}
                      {isBookingError && (
                        <a href={`mailto:${contactEmail}`} className={cn(SOFT_BUTTON, FOCUS)}>
                          <Mail className="h-[17px] w-[17px] flex-shrink-0" strokeWidth={2.2} aria-hidden="true" />
                          {t('bookingInvalidated.contactCta')}
                          <ArrowRight className="h-4 w-4 flex-shrink-0" strokeWidth={2.4} aria-hidden="true" />
                        </a>
                      )}
                      {/* Generic API error (e.g. wrong credentials / booking from
                          external portal): keep the existing Go-to-Check-In hint. */}
                      {canClaim && (
                        <Notice
                          tone="amber"
                          icon={<Info strokeWidth={2.2} />}
                          action={(
                            <button
                              type="button"
                              onClick={() => {
                                handleClose();
                                window.location.href = '/check-in';
                              }}
                              className={cn(TEXT_LINK, TONES.amber.text)}
                            >
                              {t('login.goToCheckIn')}
                              <ArrowRight className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />
                            </button>
                          )}
                        >
                          {t('login.externalBookingHint')}
                        </Notice>
                      )}
                      {/* Arrivo lontano e check-in online ancora chiuso: l'unica
                          strada è farsi collegare la prenotazione dal gestore. */}
                      {canClaim && (
                        <button type="button" onClick={goToClaim} className={cn(SOFT_BUTTON, FOCUS)}>
                          <Link2 className="h-[17px] w-[17px] flex-shrink-0" strokeWidth={2.2} aria-hidden="true" />
                          {t('claim.openButton')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* La barra fissa in fondo: Annulla + Accedi, sopra il margine sicuro. */}
            <div
              className="grid flex-shrink-0 grid-cols-[auto_minmax(0,1fr)] gap-2.5 border-t border-[#DCE6F2] bg-nva-card/95 px-4 pt-3 shadow-[0_-8px_24px_rgba(42,38,32,.08)] dark:border-stone-800 dark:bg-stone-900/95 dark:shadow-none"
              style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}
            >
              <button
                type="button"
                onClick={handleClose}
                aria-disabled={busy || undefined}
                className={cn(
                  SERVICE_BUTTON,
                  'min-w-[112px] border-[1.5px] border-nva-foot bg-nva-card px-3.5 text-nva-espresso',
                  'dark:border-stone-600 dark:bg-stone-900 dark:text-stone-100',
                  busy && 'cursor-default opacity-45 motion-safe:active:scale-100',
                )}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                aria-disabled={busy || undefined}
                className={cn(
                  SERVICE_BUTTON,
                  'bg-gradient-to-br from-blue-600 to-indigo-600 shadow-[0_8px_18px_rgba(37,99,235,.25)] dark:shadow-none',
                  busy && 'cursor-default motion-safe:active:scale-100',
                )}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-[17px] w-[17px] flex-shrink-0 motion-safe:animate-spin" strokeWidth={2.4} aria-hidden="true" />
                    {t('login.loggingIn')}
                  </>
                ) : (
                  <>
                    <LogIn className="h-[17px] w-[17px] flex-shrink-0" strokeWidth={2.2} aria-hidden="true" />
                    {t('login.loginButton')}
                  </>
                )}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default GuestLoginModal;
